import React, { useState, useMemo, useEffect } from "react";
import { Select, Upload, Button, Table, Tag, Avatar } from "antd";
import {
  InboxOutlined,
  FileExcelOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
  DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { GetAllTasksByCycleApi } from "../../api/tasks/GetAllTasksByCycleApi";
import GetPositionsPerformanceApi from "../../api/individuals/GetPositionsPerformanceApi";
import BulkCreateTasksApi from "../../api/tasks/BulkCreateTasksApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import moment from "moment";
import * as XLSX from "xlsx";

const { Option } = Select;
const { Dragger } = Upload;

const MAX_ROWS = 200;
const BATCH_SIZE = 50;

function parseDate(value) {
  if (!value) return null;
  if (typeof value === "number") {
    const date = XLSX.SSF.parse_date_code(value);
    if (date) return moment({ year: date.y, month: date.m - 1, day: date.d }).format("YYYY-MM-DD");
  }
  const formats = ["DD/MM/YYYY", "DD.MM.YYYY", "YYYY-MM-DD", "YYYY.MM.DD", "MM/DD/YYYY", "DD-MM-YYYY"];
  const parsed = moment(value, formats, true);
  if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  return null;
}

function fuzzyMatch(input, name) {
  if (!input || !name) return false;
  const a = input.toLowerCase().trim();
  const b = name.toLowerCase().trim();
  if (b.includes(a) || a.includes(b)) return true;
  // Check if all words in input appear in name
  const words = a.split(/\s+/);
  return words.every(w => b.includes(w));
}

export default function UploadTasks() {
  const [splitPlans, setSplitPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [fileName, setFileName] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(null); // { done: N, total: N } or null

  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    if (perfCycleState?.id) {
      fetchSplitPlans();
      fetchPositions();
    }
  }, [perfCycleState]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchSplitPlans() {
    setPlansLoading(true);
    const response = await GetAllTasksByCycleApi(perfCycleState.id);
    if (response.success) {
      const planMap = new Map();
      (response.data || []).forEach((task) => {
        if (task.plan && task.plan.splitFrom && !planMap.has(task.plan.id)) {
          planMap.set(task.plan.id, { id: task.plan.id, title: task.plan.title, splitFrom: task.plan.splitFrom });
        }
      });
      setSplitPlans(Array.from(planMap.values()));
    }
    setPlansLoading(false);
  }

  async function fetchPositions() {
    setPositionsLoading(true);
    const response = await GetPositionsPerformanceApi(perfCycleState.id, 1, positionState.id, 200);
    if (response.message === "success") setPositions(response.data?.positions || []);
    setPositionsLoading(false);
  }

  // Build a flat list of users for matching
  const userList = useMemo(() => {
    return positions.map(pos => ({
      positionId: pos.id,
      name: `${pos.users?.firstName || ""} ${pos.users?.lastName || ""}`.trim(),
      firstName: pos.users?.firstName || "",
      lastName: pos.users?.lastName || "",
      imageUrl: pos.users?.imageUrl || null,
      designation: pos.designations?.name || pos.designations?.abbreviation || "",
      unitName: pos.units?.name || "",
      unitId: pos.units?.id || pos.unitId || "",
    })).filter(u => u.name);
  }, [positions]);

  function matchUser(assignToText, unitText) {
    if (!assignToText) return null;
    // Try exact-ish match first
    let match = userList.find(u => fuzzyMatch(assignToText, u.name));
    // If unit is provided, prefer matches from that unit
    if (!match && unitText) {
      const unitUsers = userList.filter(u => fuzzyMatch(unitText, u.unitName));
      match = unitUsers.find(u => fuzzyMatch(assignToText, u.name));
    }
    return match || null;
  }

  function handleFile(file) {
    setParseError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (jsonData.length === 0) {
          setParseError("The file is empty or has no data rows.");
          setParsedData([]);
          return;
        }

        if (jsonData.length > MAX_ROWS) {
          setParseError(`File has ${jsonData.length} rows. Maximum allowed is ${MAX_ROWS}. Only the first ${MAX_ROWS} rows will be processed.`);
          jsonData.splice(MAX_ROWS);
        }

        const rows = jsonData.map((row, index) => {
          const keys = Object.keys(row);
          const find = (patterns) => {
            const key = keys.find((k) => patterns.some((p) => k.toLowerCase().trim().includes(p)));
            return key ? row[key] : "";
          };

          const task = find(["task", "name", "title"]);
          const startDateRaw = find(["start"]);
          const endDateRaw = find(["target", "end", "due"]);
          const unit = find(["unit", "department"]);
          const assignTo = find(["assign", "person", "employee", "position"]);

          const startDate = parseDate(startDateRaw);
          const targetDate = parseDate(endDateRaw);

          // Try to auto-match the user
          const matchedUser = matchUser(
            typeof assignTo === "string" ? assignTo.trim() : String(assignTo),
            typeof unit === "string" ? unit.trim() : String(unit)
          );

          const parsed = {
            key: index,
            task: typeof task === "string" ? task.trim() : String(task),
            startDate,
            targetDate,
            unit: typeof unit === "string" ? unit.trim() : String(unit),
            assignToRaw: typeof assignTo === "string" ? assignTo.trim() : String(assignTo),
            matchedPositionId: matchedUser?.positionId || null,
            matchedUserName: matchedUser?.name || null,
            matchedUserImage: matchedUser?.imageUrl || null,
            assignStatus: matchedUser ? "matched" : (assignTo && assignTo.toString().trim() ? "unmatched" : "empty"),
            rawStartDate: startDateRaw,
            rawTargetDate: endDateRaw,
          };

          // Validate
          const errors = [];
          if (!parsed.task) errors.push("Task name is required");
          if (!parsed.targetDate) {
            errors.push(parsed.rawTargetDate ? "Target date format invalid" : "Target date required");
          }
          if (parsed.rawStartDate && !parsed.startDate) errors.push("Start date format invalid");
          parsed.errors = errors;
          return parsed;
        });

        setParsedData(rows);
        setFileName(file.name);
      } catch (err) {
        console.error("Parse error:", err);
        setParseError("Failed to parse the file. Ensure it's a valid .xlsx or .xls file.");
        setParsedData([]);
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  }

  function handleUnitChange(rowKey, unitName) {
    setParsedData(prev => prev.map(row => {
      if (row.key !== rowKey) return row;
      const updated = { ...row, unit: unitName || "" };
      // If the currently assigned user is not in the new unit, clear them
      if (updated.matchedPositionId && unitName) {
        const user = userList.find(u => u.positionId === updated.matchedPositionId);
        if (user && user.unitName.toLowerCase() !== unitName.toLowerCase()) {
          updated.matchedPositionId = null;
          updated.matchedUserName = null;
          updated.matchedUserImage = null;
          updated.assignStatus = updated.assignToRaw ? "unmatched" : "empty";
        }
      }
      return updated;
    }));
  }

  function handleAssignChange(rowKey, positionId) {
    setParsedData(prev => prev.map(row => {
      if (row.key !== rowKey) return row;
      if (!positionId) {
        return { ...row, matchedPositionId: null, matchedUserName: null, matchedUserImage: null, assignStatus: row.assignToRaw ? "unmatched" : "empty" };
      }
      const user = userList.find(u => u.positionId === positionId);
      return {
        ...row,
        matchedPositionId: positionId,
        matchedUserName: user?.name || null,
        matchedUserImage: user?.imageUrl || null,
        assignStatus: "matched",
      };
    }));
  }

  function clearFile() {
    setParsedData([]);
    setFileName(null);
    setParseError(null);
  }

  function downloadTemplate() {
    // Build user and unit lists from loaded positions
    const userNames = [...new Set(userList.map(u => u.name).filter(Boolean))].sort();
    const unitNames = [...new Set(userList.map(u => u.unitName).filter(Boolean))].sort();

    // Create template with sample rows using real names
    const templateData = [
      { "Task": "Launch email campaign", "Start Date": "2026-07-01", "Target Date": "2026-07-15", "Unit": unitNames[0] || "Marketing", "Assign To": userNames[0] || "John Smith" },
      { "Task": "Build prospect database", "Start Date": "2026-07-05", "Target Date": "2026-07-20", "Unit": unitNames[0] || "Sales", "Assign To": userNames[1] || "Jane Doe" },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 35 }, // Task
      { wch: 14 }, // Start Date
      { wch: 14 }, // Target Date
      { wch: 20 }, // Unit
      { wch: 25 }, // Assign To
    ];

    // Create a reference sheet with all available users and units
    const maxRows = Math.max(userNames.length, unitNames.length);
    const refData = [];
    for (let i = 0; i < maxRows; i++) {
      refData.push({
        "Available Users (copy to Assign To column)": userNames[i] || "",
        "Available Units (copy to Unit column)": unitNames[i] || "",
      });
    }
    const wsRef = XLSX.utils.json_to_sheet(refData);
    wsRef['!cols'] = [{ wch: 40 }, { wch: 40 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tasks");
    XLSX.utils.book_append_sheet(wb, wsRef, "Reference - Users & Units");
    XLSX.writeFile(wb, "task_upload_template.xlsx");
  }

  async function handleSubmit() {
    if (!selectedPlan || validCount === 0) return;
    setSubmitting(true);
    setSubmitProgress({ done: 0, total: validCount });

    const tasksToCreate = parsedData
      .filter(r => r.errors.length === 0)
      .map(r => ({
        name: r.task,
        taskStartDate: r.startDate || undefined,
        taskEndDate: r.targetDate,
        assignedTo: r.matchedPositionId || undefined,
      }));

    let totalCreated = 0;
    let failed = false;

    // Process in batches
    for (let i = 0; i < tasksToCreate.length; i += BATCH_SIZE) {
      const batch = tasksToCreate.slice(i, i + BATCH_SIZE);
      const res = await BulkCreateTasksApi(selectedPlan, batch);
      if (res.success) {
        totalCreated += batch.length;
        setSubmitProgress({ done: totalCreated, total: validCount });
      } else {
        openNotificationError("bottomRight", res.message || `Failed at batch ${Math.floor(i / BATCH_SIZE) + 1}`);
        failed = true;
        break;
      }
    }

    if (!failed) {
      openNotificationSuccess("bottomRight", `${totalCreated} tasks created successfully`);
      clearFile();
    } else if (totalCreated > 0) {
      openNotificationSuccess("bottomRight", `${totalCreated} tasks created before error occurred`);
    }

    setSubmitting(false);
    setSubmitProgress(null);
  }

  const validCount = parsedData.filter((r) => r.errors.length === 0).length;
  const errorCount = parsedData.filter((r) => r.errors.length > 0).length;
  const unmatchedCount = parsedData.filter(r => r.assignStatus === "unmatched").length;
  const selectedPlanData = splitPlans.find((p) => p.id === selectedPlan);

  const columns = [
    { title: "#", dataIndex: "key", width: 45, render: (val) => val + 1 },
    {
      title: "Task",
      dataIndex: "task",
      ellipsis: true,
      render: (text, record) => (
        <span style={{ color: !text ? "#ef4444" : "#1e293b", fontWeight: 500 }}>
          {text || <span style={{ fontStyle: "italic", color: "#ef4444" }}>Missing</span>}
        </span>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      width: 110,
      render: (text, record) => {
        if (record.rawStartDate && !text) return <span style={{ color: "#ef4444", fontSize: 11 }}>{String(record.rawStartDate)} — invalid</span>;
        return text || <span style={{ color: "#94a3b8" }}>—</span>;
      },
    },
    {
      title: "Target Date",
      dataIndex: "targetDate",
      width: 110,
      render: (text, record) => {
        if (!text && record.rawTargetDate) return <span style={{ color: "#ef4444", fontSize: 11 }}>{String(record.rawTargetDate)} — invalid</span>;
        if (!text) return <span style={{ color: "#ef4444", fontStyle: "italic", fontSize: 11 }}>Missing</span>;
        return text;
      },
    },
    {
      title: "Unit",
      width: 150,
      render: (_, record) => {
        const unitOptions = [...new Set(userList.map(u => u.unitName).filter(Boolean))].sort();
        return (
          <Select
            size="small"
            style={{ width: "100%" }}
            value={record.unit || undefined}
            placeholder="Select unit"
            showSearch
            allowClear
            optionFilterProp="children"
            onChange={(val) => handleUnitChange(record.key, val)}
          >
            {unitOptions.map(u => (
              <Option key={u} value={u}>{u}</Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Assign To",
      width: 220,
      render: (_, record) => {
        // Filter users by the selected unit for this row
        const filteredUsers = record.unit
          ? userList.filter(u => u.unitName.toLowerCase() === record.unit.toLowerCase())
          : userList;
        const options = filteredUsers.length > 0 ? filteredUsers : userList;

        // Ensure the matched user is always in the options even if filtered by unit
        const matchedUser = record.matchedPositionId ? userList.find(u => u.positionId === record.matchedPositionId) : null;
        const finalOptions = matchedUser && !options.find(o => o.positionId === matchedUser.positionId)
          ? [matchedUser, ...options]
          : options;

        return (
          <Select
            size="small"
            style={{ width: "100%" }}
            value={record.matchedPositionId || undefined}
            placeholder={record.assignStatus === "unmatched" ? `"${record.assignToRaw}" not found` : "Select user"}
            showSearch
            allowClear
            optionFilterProp="label"
            optionLabelProp="label"
            onChange={(val) => handleAssignChange(record.key, val)}
            status={record.assignStatus === "unmatched" ? "warning" : undefined}
          >
            {finalOptions.map(u => (
              <Option key={u.positionId} value={u.positionId} label={u.name}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {u.imageUrl ? <Avatar src={u.imageUrl} size={18} /> : <Avatar size={18} style={{ backgroundColor: "#0f172a", fontSize: 8 }}>{u.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}</Avatar>}
                  <span style={{ fontSize: 12 }}>{u.name}</span>
                  {u.designation && <span style={{ fontSize: 10, color: "#94a3b8" }}>· {u.designation}</span>}
                </div>
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Status",
      width: 80,
      render: (_, record) =>
        record.errors.length === 0 ? (
          <Tag color="success" icon={<CheckCircleFilled />}>Valid</Tag>
        ) : (
          <Tag color="error" icon={<CloseCircleFilled />}>Error</Tag>
        ),
    },
  ];

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1a202c", margin: 0 }}>Upload Tasks</h2>
        <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>Bulk create tasks from an Excel file for a split plan</div>
      </div>

      {/* Plan selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4, fontWeight: 600, textTransform: "uppercase" }}>Split Plan</div>
          <Select
            value={selectedPlan}
            onChange={(val) => setSelectedPlan(val)}
            placeholder="Select a split plan"
            style={{ width: 400 }}
            size="small"
            showSearch
            optionFilterProp="children"
            loading={plansLoading}
            allowClear
          >
            {splitPlans.map((plan) => (
              <Option key={plan.id} value={plan.id}>{plan.title}</Option>
            ))}
          </Select>
        </div>
      </div>

      {/* Upload area */}
      {!fileName ? (
        <div style={{ marginBottom: 20 }}>
          <Dragger
            accept=".xlsx,.xls"
            showUploadList={false}
            beforeUpload={handleFile}
            style={{ padding: "30px 20px", borderColor: "#94a3b8", borderRadius: 8 }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: "#64748b", fontSize: 40 }} /></p>
            <p style={{ fontSize: 15, color: "#334155", fontWeight: 600 }}>Drag & drop your .xlsx file here</p>
            <p style={{ fontSize: 13, color: "#94a3b8" }}>or click to browse. Accepted: .xlsx, .xls</p>
          </Dragger>
          {parseError && <div style={{ color: "#ef4444", fontSize: 13, marginTop: 8 }}>{parseError}</div>}
          <div style={{ marginTop: 12 }}>
            <Button type="link" icon={<DownloadOutlined />} onClick={downloadTemplate} style={{ padding: 0, fontSize: 13 }}>
              Download Excel template
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", backgroundColor: "#f0fdf4", border: "1px solid #a7f3d0", borderRadius: 8, marginBottom: 20 }}>
          <FileExcelOutlined style={{ fontSize: 22, color: "#16a34a" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 14 }}>{fileName}</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{parsedData.length} rows parsed</div>
          </div>
          <Button size="small" icon={<DeleteOutlined />} onClick={clearFile} danger>Remove</Button>
        </div>
      )}

      {/* Preview table */}
      {parsedData.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, padding: "10px 16px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 13 }}>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>Preview</span>
            <Tag color="success">{validCount} valid</Tag>
            {errorCount > 0 && <Tag color="error">{errorCount} errors</Tag>}
            {unmatchedCount > 0 && <Tag color="warning">{unmatchedCount} unmatched users</Tag>}
          </div>

          <Table
            columns={columns}
            dataSource={parsedData}
            pagination={false}
            size="small"
            bordered
            style={{ marginBottom: 24 }}
            rowClassName={(record) => record.errors.length > 0 ? "bg-red-50" : ""}
          />

          {/* Submit */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24, alignItems: "center" }}>
            {submitProgress && (
              <span style={{ fontSize: 12, color: "#2563eb", fontWeight: 500 }}>
                Creating... {submitProgress.done}/{submitProgress.total}
              </span>
            )}
            <Button onClick={clearFile} disabled={submitting}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedPlan || validCount === 0}
              loading={submitting}
              onClick={handleSubmit}
            >
              Create {validCount} Task{validCount !== 1 ? "s" : ""}
            </Button>
          </div>

          {!selectedPlan && (
            <div style={{ textAlign: "right", marginTop: 8, fontSize: 12, color: "#f59e0b" }}>
              <WarningOutlined /> Please select a split plan before creating tasks
            </div>
          )}
        </>
      )}
    </div>
  );
}
